import { analysisDisplayText } from '../utils/analysisDisplayText';
import React, { useEffect, useState } from 'react';
import { ChevronDown, Target } from 'lucide-react';
import './Phase5ReportSystem.css';

export const phase5Tone = (index = 0) => `phase5-tone-${(index % 5) + 1}`;
export const REPORT_FONT_STORAGE_KEY = 'insight_report_font_family';
export const REPORT_TEXT_SIZE_STORAGE_KEY = 'insight_report_text_size';
export const REPORT_FONTS = Object.freeze({
  system: { label: 'System Default', family: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  segoe: { label: 'Segoe UI', family: '"Segoe UI", Arial, sans-serif' },
  arial: { label: 'Arial', family: 'Arial, Helvetica, sans-serif' },
  helvetica: { label: 'Helvetica', family: 'Helvetica, Arial, sans-serif' },
  helveticaNeue: { label: 'Helvetica Neue', family: '"Helvetica Neue", Helvetica, Arial, sans-serif' },
  arialNarrow: { label: 'Arial Narrow', family: '"Arial Narrow", Arial, sans-serif' },
  calibri: { label: 'Calibri', family: 'Calibri, "Segoe UI", sans-serif' },
  georgia: { label: 'Georgia', family: 'Georgia, "Times New Roman", serif' },
  times: { label: 'Times New Roman', family: '"Times New Roman", Times, serif' },
  cambria: { label: 'Cambria', family: 'Cambria, Georgia, serif' },
  garamond: { label: 'Garamond', family: 'Garamond, "Times New Roman", serif' },
  verdana: { label: 'Verdana', family: 'Verdana, Arial, sans-serif' },
  tahoma: { label: 'Tahoma', family: 'Tahoma, "Segoe UI", sans-serif' },
  trebuchet: { label: 'Trebuchet MS', family: '"Trebuchet MS", Arial, sans-serif' },
  courier: { label: 'Courier New', family: '"Courier New", Courier, monospace' },
  century: { label: 'Century Gothic', family: '"Century Gothic", Arial, sans-serif' },
  bookAntiqua: { label: 'Book Antiqua', family: '"Book Antiqua", Palatino, Georgia, serif' },
  palatino: { label: 'Palatino Linotype', family: '"Palatino Linotype", Palatino, Georgia, serif' },
  franklin: { label: 'Franklin Gothic', family: '"Franklin Gothic Medium", Arial, sans-serif' },
  gillSans: { label: 'Gill Sans', family: '"Gill Sans", "Segoe UI", sans-serif' },
  lucidaSans: { label: 'Lucida Sans', family: '"Lucida Sans Unicode", "Lucida Grande", sans-serif' },
  lucidaConsole: { label: 'Lucida Console', family: '"Lucida Console", Monaco, monospace' },
  baskerville: { label: 'Baskerville', family: 'Baskerville, Georgia, serif' },
  candara: { label: 'Candara', family: 'Candara, "Segoe UI", sans-serif' },
  corbel: { label: 'Corbel', family: 'Corbel, "Segoe UI", sans-serif' },
  constantia: { label: 'Constantia', family: 'Constantia, Cambria, Georgia, serif' },
  avenir: { label: 'Avenir', family: 'Avenir, "Segoe UI", Arial, sans-serif' },
  futura: { label: 'Futura', family: 'Futura, "Century Gothic", Arial, sans-serif' },
  optima: { label: 'Optima', family: 'Optima, "Segoe UI", Arial, sans-serif' },
  rockwell: { label: 'Rockwell', family: 'Rockwell, "Courier New", serif' },
  bookman: { label: 'Bookman', family: 'Bookman, "Bookman Old Style", Georgia, serif' },
  consolas: { label: 'Consolas', family: 'Consolas, "Courier New", monospace' },
  monaco: { label: 'Monaco', family: 'Monaco, Consolas, "Courier New", monospace' },
  menlo: { label: 'Menlo', family: 'Menlo, Monaco, Consolas, monospace' },
  msSansSerif: { label: 'MS Sans Serif', family: '"MS Sans Serif", Arial, sans-serif' },
  msSerif: { label: 'MS Serif', family: '"MS Serif", "Times New Roman", serif' },
});
export const REPORT_TEXT_SIZES = Object.freeze({
  small: { label: 'Small', body: '14px', subheading: '13px', section: '14px', title: '21px', lineHeight: '1.6' },
  medium: { label: 'Medium', body: '15.5px', subheading: '13.5px', section: '15px', title: '22px', lineHeight: '1.65' },
  large: { label: 'Large', body: '17px', subheading: '15px', section: '16px', title: '23px', lineHeight: '1.65' },
  extraLarge: { label: 'Extra Large', body: '18.5px', subheading: '16px', section: '17px', title: '24px', lineHeight: '1.65' },
});

const DEFAULT_REPORT_FONT = 'segoe';
const REPORT_FONT_EVENT = 'insight-report-font-change';
const REPORT_TEXT_SIZE_EVENT = 'insight-report-text-size-change';

const readReportFont = () => {
  if (typeof window === 'undefined') return DEFAULT_REPORT_FONT;
  try {
    const stored = window.localStorage.getItem(REPORT_FONT_STORAGE_KEY);
    return Object.prototype.hasOwnProperty.call(REPORT_FONTS, stored) ? stored : DEFAULT_REPORT_FONT;
  } catch (_error) {
    return DEFAULT_REPORT_FONT;
  }
};

const applyReportFont = (fontKey) => {
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--insight-report-font-family', REPORT_FONTS[fontKey].family);
  }
};

const readReportTextSize = () => {
  if (typeof window === 'undefined') return 'medium';
  try {
    const stored = window.localStorage.getItem(REPORT_TEXT_SIZE_STORAGE_KEY);
    return Object.prototype.hasOwnProperty.call(REPORT_TEXT_SIZES, stored) ? stored : 'medium';
  } catch (_error) { return 'medium'; }
};

const applyReportTextSize = (sizeKey) => {
  if (typeof document === 'undefined') return;
  const size = REPORT_TEXT_SIZES[sizeKey];
  document.documentElement.style.setProperty('--insight-report-body-size', size.body);
  document.documentElement.style.setProperty('--insight-report-line-height', size.lineHeight);
  document.documentElement.style.setProperty('--insight-report-subheading-size', size.subheading);
  document.documentElement.style.setProperty('--insight-report-section-title-size', size.section);
  document.documentElement.style.setProperty('--insight-report-title-size', size.title);
};

export const getReportHeadingPresentation = (title, fallbackNumber) => {
  const text = String(title || '').trim();
  const sourceNumber = text.match(/^\s*(\d+(?:\.\d+)*)[.)]?\s+/);
  return {
    number: sourceNumber?.[1] || fallbackNumber,
    title: sourceNumber ? text.slice(sourceNumber[0].length).trim() : text,
  };
};

export function ReportFontSelector() {
  const [fontKey, setFontKey] = useState(readReportFont);

  useEffect(() => {
    applyReportFont(fontKey);
    const sync = (event) => {
      if (Object.prototype.hasOwnProperty.call(REPORT_FONTS, event.detail)) setFontKey(event.detail);
    };
    window.addEventListener(REPORT_FONT_EVENT, sync);
    return () => window.removeEventListener(REPORT_FONT_EVENT, sync);
  }, [fontKey]);

  const changeFont = (event) => {
    const next = event.target.value;
    if (!Object.prototype.hasOwnProperty.call(REPORT_FONTS, next)) return;
    setFontKey(next);
    applyReportFont(next);
    try { window.localStorage.setItem(REPORT_FONT_STORAGE_KEY, next); } catch (_error) { /* preference storage may be unavailable */ }
    window.dispatchEvent(new CustomEvent(REPORT_FONT_EVENT, { detail: next }));
  };

  return <div className="phase5-font-control">
    <label htmlFor="report-font-select">Font:</label>
    <select id="report-font-select" value={fontKey} onChange={changeFont} aria-label="Analysis font">
      {Object.entries(REPORT_FONTS).map(([key, font]) => <option key={key} value={key}>{font.label}</option>)}
    </select>
  </div>;
}

export function ReportTextSizeSelector() {
  const [sizeKey, setSizeKey] = useState(readReportTextSize);
  useEffect(() => {
    applyReportTextSize(sizeKey);
    const sync = (event) => {
      if (Object.prototype.hasOwnProperty.call(REPORT_TEXT_SIZES, event.detail)) setSizeKey(event.detail);
    };
    window.addEventListener(REPORT_TEXT_SIZE_EVENT, sync);
    return () => window.removeEventListener(REPORT_TEXT_SIZE_EVENT, sync);
  }, [sizeKey]);
  const changeSize = (event) => {
    const next = event.target.value;
    if (!Object.prototype.hasOwnProperty.call(REPORT_TEXT_SIZES, next)) return;
    setSizeKey(next); applyReportTextSize(next);
    try { window.localStorage.setItem(REPORT_TEXT_SIZE_STORAGE_KEY, next); } catch (_error) { /* preference storage may be unavailable */ }
    window.dispatchEvent(new CustomEvent(REPORT_TEXT_SIZE_EVENT, { detail: next }));
  };
  return <div className="phase5-font-control phase5-size-control">
    <label htmlFor="report-text-size-select">Text Size:</label>
    <select id="report-text-size-select" value={sizeKey} onChange={changeSize} aria-label="Analysis text size">
      {Object.entries(REPORT_TEXT_SIZES).map(([key, size]) => <option key={key} value={key}>{size.label}</option>)}
    </select>
  </div>;
}

export function Phase5ReportToolbar({
  reportKey, reportName, onExpandAll, onCollapseAll, expandDisabled = false, collapseDisabled = false,
  showSectionControls = true,
}) {
  return <div className="phase5-shared-toolbar" data-report-key={reportKey} data-testid={`phase5-toolbar-${reportKey}`}>
    {showSectionControls && <div className="phase5-report-toolbar" role="group" aria-label={`${analysisDisplayText(reportName)} Analysis section controls`}>
      <button type="button" className="phase5-report-toolbar-button" onClick={onExpandAll} disabled={expandDisabled} aria-label={`Expand all ${analysisDisplayText(reportName)} Analysis sections`}>Expand All</button>
      <span className="phase5-report-toolbar-separator" aria-hidden="true">|</span>
      <button type="button" className="phase5-report-toolbar-button" onClick={onCollapseAll} disabled={collapseDisabled} aria-label={`Collapse all ${analysisDisplayText(reportName)} Analysis sections`}>Collapse All</button>
    </div>}
    <ReportFontSelector />
    <ReportTextSizeSelector />
  </div>;
}

export function Phase5ReportMetadata({ generatedBy, generatedAt, generatedByLabel = 'Generated by', generatedAtLabel = 'Generated' }) {
  if (!generatedBy && !generatedAt) return null;
  return <div className="phase5-report-metadata" data-testid="phase5-report-metadata">
    {generatedBy && <p className="phase5-report-generated-by">{generatedByLabel}: <span>{generatedBy}</span></p>}
    {generatedAt && <p className="phase5-report-generated"><span className="phase5-report-meta-label">{generatedAtLabel}:</span>{' '}{new Date(generatedAt).toLocaleString()}</p>}
  </div>;
}

export function Phase5ReportTitle({ title, subtitle, generatedBy, generatedAt, generatedByLabel, generatedAtLabel, actions, icon: Icon = Target }) {
  return <header className="phase5-report-titlebar">
    <div className="phase5-report-titlecopy"><span className="phase5-report-icon" aria-hidden="true"><Icon /></span><div>
      <h4 className="phase5-report-title">{analysisDisplayText(title)}</h4>
      {subtitle && <p className="phase5-report-subtitle">{subtitle}</p>}
      <Phase5ReportMetadata generatedBy={generatedBy} generatedAt={generatedAt} generatedByLabel={generatedByLabel} generatedAtLabel={generatedAtLabel} />
    </div></div>
    {actions && <div className="phase5-report-actions">{actions}</div>}
  </header>;
}

export function Phase5SectionHeading({ index, displayNumber, children, expanded }) {
  const number = displayNumber || index + 1;
  return <span className="phase5-section-heading">
    <span className="phase5-section-badge" data-number={number} aria-hidden="true">{number}.{' '}</span>
    <span className="phase5-section-title">{children}</span>
    <ChevronDown className={`phase5-section-chevron${expanded === false ? '' : ' is-expanded'}`} aria-hidden="true" />
  </span>;
}

export function Phase5ReportSection({ index, title, expanded, onToggle, children, testId }) {
  const heading = getReportHeadingPresentation(title, index + 1);
  return <section className={`phase5-report-section ${phase5Tone(index)}`} data-testid={testId}>
    <button type="button" className="phase5-section-button" aria-expanded={expanded} onClick={onToggle}>
      <Phase5SectionHeading index={index} displayNumber={heading.number} expanded={expanded}>{heading.title}</Phase5SectionHeading>
    </button>
    {expanded && <div className="phase5-section-body">{children}</div>}
  </section>;
}
